<template>
  <BasePageContainer>
    <div class="calendar d-flex justify-content-center align-items-center mb-2">
      <button
        class="calendar__btn calendar__btn--previous"
        @click="onClickCalendarBtn">
        <span
          class="material-icons calendar__icon">
          arrow_left
        </span>
      </button>
      <span class="ml-1 mr-1">
        <base-datepicker
          :value="selectedDate"
          @input="setSelectedDate" />
      </span>
      <button
        class="calendar__btn calendar__btn--next"
        @click="onClickCalendarBtn">
        <span class="calendar__icon material-icons">
          arrow_right
        </span>
      </button>
    </div>
    <template v-if="isDisplayDetail">
      <event-date-card
        v-for="date in datesOfSelectedMonth"
        :ref="formatYYYYMMDD(date)"
        :key="formatYYYYMMDD(date)"
        :is-manager="isManager"
        :date="date">
        <div
          v-if="formatYYYYMMDD(date) in eventsByYYYYMMDD"
          class="event-list pt-3">
          <event
            v-for="event in eventsByYYYYMMDD[formatYYYYMMDD(date)]"
            :key="event.id"
            :ref="event.id"
            :event="event"
            :is-manager="isManager"
            :fetch-detail="() => refetchEventDetail(event.id)">
            <div>
              <event-detail
                :event="event"
                :is-manager="isManager"
                :is-parent-member="isParentMember"
                :on-attend="() => attendEvent(event)"
                :on-absence="() => absenceEvent(event)"
                :on-cancel-attend="() => cancelAttendEvent(event)"
                :on-set-stamp="(icon) => setStamp(event, icon)"
                :on-delete-event="() => deleteEvent(event)" />
              <ul class="event-comments d-flex flex-column p-0 m-0">
                <event-comment-item
                  v-for="comment in event.eventComments"
                  :key="comment.id"
                  :ref="comment.id"
                  :comment="comment"
                  :delete-comment="() => deleteComment(event.id, comment.id)" />
              </ul>
              <event-comment-input
                v-if="!isParentMember"
                ref="form"
                :event-id="event.id"
                :refetch-event="refetchEventDetail"
                @create="addComment" />
            </div>
          </event>
        </div>
      </event-date-card>
    </template>
    <template v-else>
      <div class="pb-4">
        <event-date-list-item
          v-for="date in datesOfSelectedMonth"
          :ref="formatYYYYMMDD(date)"
          :key="formatYYYYMMDD(date)"
          :is-manager="isManager"
          :date="date"
          @click="toDetailDate(date)">
          <template v-if="formatYYYYMMDD(date) in eventsByYYYYMMDD">
            <event-list-item
              v-for="event in eventsByYYYYMMDD[formatYYYYMMDD(date)]"
              :key="event.id"
              :ref="event.id"
              :event="event"
              @click="toDetailEvent(event)" />
          </template>
          <template v-else>
            ---
          </template>
        </event-date-list-item>
      </div>
    </template>
    <div
      v-if="isManager"
      class="pt-4 d-flex flex-column">
      <b-button
        style="width: 180px;"
        class="mb-2 font-weight-bold"
        variant="secondary"
        to="/admin/categories">
        カテゴリー管理
      </b-button>
      <b-button
        class="font-weight-bold"
        style="width: 180px;"
        variant="secondary"
        to="/admin/templates">
        テンプレート管理
      </b-button>
    </div>
  </BasePageContainer>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import EventDateCard from '@/components/view/EventDateCard.vue'
import EventDateListItem from '@/components/view/EventDateListItem.vue'
import EventListItem from '@/components/view/EventListItem.vue'
import Event from '@/components/view/Event.vue'
import EventDetail from '@/components/view/EventDetail.vue'
import EventCommentItem from '@/components/view/EventCommentItem.vue'
import EventCommentInput from '@/components/view/EventCommentInput.vue'
import BaseDatepicker from '@/components/parts/BaseDatepicker.vue'
import format from 'date-fns/format'
import addDays from 'date-fns/addDays'
import addMonths from 'date-fns/addMonths'
import startOfMonth from 'date-fns/startOfMonth'
import endOfMonth from 'date-fns/endOfMonth'
import getDaysInMonth from 'date-fns/getDaysInMonth'
import confirmIfFormsChanged from '@/components/mixin/confirmIfFormsChanged'
import preventBlankPage from '@/components/mixin/preventBlankPage'

export default {
  components: {
    BaseDatepicker,
    EventDateCard,
    Event,
    EventDetail,
    EventCommentItem,
    EventCommentInput,
    EventDateListItem,
    EventListItem
  },
  mixins: [confirmIfFormsChanged(), preventBlankPage],
  data() {
    return {
      events: [],
      initialized: false,
    }
  },
  computed: {
    ...mapState(['isLoading']),
    ...mapState('events', [
      'isDisplayDetail',
      'selectedDate',
      'selectedCategories',
      'shouldScrollToToday',
      'shouldScrollToTop',
      'shouldScrollToSelectedDate',
      'pinnedCategories'
    ]),
    ...mapGetters('events', ['categoryIds']),
    ...mapGetters('auth', ['isManager', 'isParentMember']),
    selectedYYYYMM() {
      return format(this.selectedDate, 'yyyyMM')
    },
    datesOfSelectedMonth() {
      const days = getDaysInMonth(this.selectedDate)
      const startDate = startOfMonth(this.selectedDate)
      return [...Array(days)].map((_, index) => addDays(startDate, index))
    },
    eventsByYYYYMMDD() {
      return this.events.reduce((prev, curr) => {
        const YYYYMMDD = this.formatYYYYMMDD(new Date(curr.startAt))
        const sameDayEventsExist = YYYYMMDD in prev
        if (sameDayEventsExist) {
          // idとピン留めされているかでソート
          const _YYYYMMDDList = [...prev[YYYYMMDD], curr].sort(
            (x, y) => {
              if (!this.isPinned(x.category.id) && this.isPinned(y.category.id)) {
                return 1
              }
              if (this.isPinned(x.category.id) && !this.isPinned(y.category.id)) {
                return -1
              }
              return 0
            }
          )
          return { ...prev, [YYYYMMDD]: _YYYYMMDDList }
        }
        return { ...prev, [YYYYMMDD]: [curr] }
      }, {})
    },
    startOfMonth() {
      return startOfMonth(this.selectedDate)
    },
    endOfMonth() {
      return endOfMonth(this.selectedDate)
    },
    selectedYYYYMMAndShouldScrollToTodayAndShouldScrollToSelectedDate() {
      return [this.selectedYYYYMM, this.shouldScrollToToday, this.shouldScrollToSelectedDate]
    }
  },
  watch: {
    async selectedCategories(newSelectedCategories, oldSelectedCategories) {
      if (newSelectedCategories.length === 0) {
        this.events = []
        return
      }

      // selectedCategoriesの中身が変化したときのみ予定一覧を取得し直す
      if (JSON.stringify(newSelectedCategories) === JSON.stringify(oldSelectedCategories)) {
        return
      }
      this.$store.commit('startLoading')
      this.events = await this.fetchEvents()
      this.$store.commit('finishLoading')
    },
    // 本日にスクロールする
    async selectedYYYYMMAndShouldScrollToTodayAndShouldScrollToSelectedDate([
      newSelectedYYYYMM,
      newShouldScrollToToday,
      newShouldScrollToSelectedDate,
    ], [
      oldSelectedYYYYMM,
    ]) {
      // createdで取得するのを邪魔しないように
      if (!this.initialized) {
        return
      }
      this.$store.commit('startLoading')
      const isChangedSelectedYYYYMM = newSelectedYYYYMM !== oldSelectedYYYYMM
      if (isChangedSelectedYYYYMM || newShouldScrollToToday || newShouldScrollToSelectedDate) {
        this.events = await this.fetchEvents().catch((e) => {
          console.error(e)
          this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
          this.$store.commit('finishLoading')
          return
        })
      }
      if (newShouldScrollToToday) {
        const today = new Date()
        this.scrollToDate(today)
        this.$store.commit('events/setShouldScrollToToday', false)
      }
      if (newShouldScrollToSelectedDate) {
        this.scrollToDate(this.selectedDate)
        this.$store.commit('events/setShouldScrollToSelectedDate', false)
      }
      
      this.$store.commit('finishLoading')
    },
    // 最上部にスクロールする
    shouldScrollToTop(newVal) {
      if (newVal) {
        this.scrollToTop()
        this.$store.commit('events/setShouldScrollToTop', false)
      }
    }
  },
  beforeDestroy() {
    this.$store.commit('events/setIsDisplayDetail', true)
    this.$store.commit('events/setSelectedDate', new Date())
  },
  async created() {
    this.$store.commit('startLoading')
    const { date, eventId, eventCommentId } = this.$route.query
    // 予定取得前に年月移動
    if (eventId !== undefined) {
      await this.setSelectedDateFromEvent(eventId)
    }
    const scrollDate = date ? new Date(date) : null
    if (scrollDate) {
      this.$store.commit('events/setSelectedDate', scrollDate)
    }

    // カテゴリ取得
    const categories = await this.fetchCategories()
    const categoryIds = categories.map(category => category.id)
    const deletedCategoryIds = this.categoryIds.filter(categoryId => !categoryIds.includes(categoryId))
    const newCategoryIds = categoryIds.filter(categoryId => !this.categoryIds.includes(categoryId))
    const newSelectedCategories = [...new Set([...this.selectedCategories, ...newCategoryIds])].filter(categoryId => !deletedCategoryIds.includes(categoryId))
    this.$store.commit('events/setCategories', categories)
    this.$store.commit('events/setSelectedCategories', newSelectedCategories)

    // テナント情報と予定取得
    const [tenantInfo, events] = await Promise.all([
      this.fetchTenantInfo(),
      this.fetchEvents()
    ]).catch((e) => {
      console.error(e)
      this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      this.$store.commit('finishLoading')
      return
    })
    this.events = events
    if (tenantInfo) {
      this.$store.commit('tenant/setTenantInfo', tenantInfo)
    }

    // 日付、予定、コメントへのスクロール位置調整
    if (eventId && eventCommentId) {
      this.scrollToEventComment(eventId, eventCommentId)
    } else if (eventId) {
      this.scrollAndOpenEvent(eventId)
    } else {
      this.scrollToDate(scrollDate || new Date())
    }
    this.initialized = true
    this.$store.commit('finishLoading')
  },
  methods: {
    deleteEvent(event) {
      if (!window.confirm(`${event.title} を削除してもよろしいですか?`)) return
      this.$axios.delete(`events/${event.id}/`).then(() => {
        const eventIndex = this.events.findIndex(item => item.id === event.id)
        this.events.splice(eventIndex, 1)
        this.$store.commit('notify/set', {message: `${event.title} を削除しました`})
      }).catch(e => {
        console.error(e)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
    },
    isPinned(categoryId) {
      return this.pinnedCategories.includes(categoryId)
    },
    async setSelectedDateFromEvent(eventId) {
      const event = await this.fetchEvent(eventId)
      this.$store.commit('events/setSelectedDate', new Date(event.startAt))
    },
    formatYYYYMMDD(date) {
      return format(date, 'yyyyMMdd')
    },
    fetchTenantInfo() {
      return this.$axios.get('/tenants/')
        .then((res) => res.data)
    },
    fetchCategories() {
      return this.$axios.get('/events/categories/', {
        params: {
          ordering: 'sort_order'
        }
      }).then(res => res.data.results)
        .catch(e => {
          console.error(e)
          this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
        })
    },
    fetchEvents() {
      if (this.selectedCategories.length === 0) {
        return Promise.resolve([])
      }
      return this.$axios.get('/events/', {
        params: {
          limit: 9999999,
          category__in: this.selectedCategories.join(','),
          start_at__gte: this.startOfMonth,
          end_at__lte: this.endOfMonth,
          ordering: 'start_at,end_at,updated_at',
          // 一般ユーザでは下書きの予定は取得しない、管理者の場合は両方取得する
          is_draft: this.isManager ? null : false
        }
        // カテゴリのsortOrderの順番に並べ替える(時刻での並べ替えはリクエスト時のorderingで指定している)
      }).then(res => res.data.results.sort((prev, next) => prev.category.sortOrder < next.category.sortOrder ? -1 : 1))
    },
    fetchEvent(eventId) {
      return this.$axios.get(`/events/${eventId}/`).then(res => res.data)
    },
    async refetchEventDetail(eventId) {
      const fetchedEvent = await this.fetchEvent(eventId)
      if (!fetchedEvent) {
        this.events = this.events.filter(event => event.id !== eventId)
        this.$store.commit('notify/set', {message: 'この予定は削除されました', type: 'danger'})
        return null
      }
      const eventIndex = this.events.findIndex(event => event.id === fetchedEvent.id)
      // 一般ユーザが下書きのイベント詳細を取得してしまった時
      if (!this.isManager && fetchedEvent.isDraft) {
        this.events = this.events.filter(event => event.id !== fetchedEvent.id)
        this.$store.commit('notify/set', {message: 'この予定は下書き状態に変更されました', type: 'danger'})
        return fetchedEvent
      }
      this.events.splice(eventIndex, 1, fetchedEvent)
      return fetchedEvent
    },
    async addComment(eventId, commentInput, file) {
      this.$store.commit('startLoading')
      const formData = new FormData()
      formData.append('event', eventId)
      formData.append('detail', commentInput)
      if (file) {
        formData.append('file', file)
      }
      
      await this.$axios.post('/event_comments/', formData).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.refetchEventDetail(eventId)
      this.$store.commit('finishLoading')
    },
    async deleteComment(eventId, commentId) {
      if (!window.confirm('コメントを削除してもよろしいですか?')) return
      this.$store.commit('startLoading')
      await this.$axios.delete(`/event_comments/${commentId}/`).then(() => {
        this.$store.commit('notify/set', {message: 'コメントを削除しました。'})
        this.refetchEventDetail(eventId)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    async attendEvent(event) {
      this.$store.commit('startLoading')
      await this.$axios.post(`/events/${event.id}/attend/`).then(() => {
        this.$store.commit('notify/set', {message: `${event.title}に参加します`})
        this.refetchEventDetail(event.id)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    async absenceEvent(event) {
      this.$store.commit('startLoading')
      await this.$axios.post(`/events/${event.id}/absence/`).then(() => {
        this.$store.commit('notify/set', {message: `${event.title}に参加しません`})
        this.refetchEventDetail(event.id)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    async setStamp(event, icon) {
      this.$store.commit('startLoading')
      await this.$axios.patch(`/events/${event.id}/`, {
        icon
      }).then(() => {
        this.refetchEventDetail(event.id)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    async cancelAttendEvent(event) {
      this.$store.commit('startLoading')
      const isAttend = event.myReply?.isAttend
      await this.$axios.delete(`/events/${event.id}/attend/`).then(() => {
        this.$store.commit('notify/set', {message: `${event.title}の${isAttend ? '参加' : '不参加'}をキャンセルしました`})
        this.refetchEventDetail(event.id)
      }).catch(err => {
        console.error(err)
        this.$store.commit('notify/set', {isUnknownError: true, type: 'danger'})
      })
      this.$store.commit('finishLoading')
    },
    onClickCalendarBtn(event) {
      if (this.isLoading) return
      this.selectedDateGoals = []
      if (event.target.innerText === 'arrow_left') {
        const prevMonthFirstDate = startOfMonth(addMonths(this.selectedDate, -1))
        this.$store.commit('events/setSelectedDate', prevMonthFirstDate)
      }
      if (event.target.innerText === 'arrow_right') {
        const nextMonthFirstDate = startOfMonth(addMonths(this.selectedDate, 1))
        this.$store.commit('events/setSelectedDate', nextMonthFirstDate)
      }
      this.scrollToTop()
    },
    setSelectedDate(date) {
      this.$store.commit('events/setSelectedDate', date)
      this.$store.commit('events/setShouldScrollToSelectedDate', true)
    },
    scrollToEl(el) {
      const rect = el.getBoundingClientRect()
      const elementTop = rect.top + window.pageYOffset
      const offset = 110
      // setTimeoutを使わないとDatepickerで選択時に移動しない
      setTimeout(() => {
        window.scrollTo(0, elementTop - offset)
      }, 1)
    },
    scrollToDate(date) {
      // 月を跨いだ日付の変更の場合のためにnextTickを使用
      this.$nextTick(() => {
        const ref = this.$refs[this.formatYYYYMMDD(date)][0]
        // refが存在しない場合は最上部にスクロール
        if (!ref) {
          this.scrollToTop()
          return
        }
        const el = ref.$el
        this.scrollToEl(el)
      })
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo(0, 0)
      })
    },
    scrollAndOpenEvent(eventId) {
      this.$nextTick(() => {
        const ref = this.$refs[eventId][0]
        ref.openEventDetail()
        this.scrollToEl(ref.$el)
      })
    },
    scrollToEventComment(eventId, eventCommentId) {
      this.$nextTick(async () => {
        const eventRef = this.$refs[eventId][0]
        await eventRef.openEventDetail()
        const commentRef = this.$refs[eventCommentId][0]
        this.scrollToEl(commentRef.$el)
      })
    },
    toDetailDate(date) {
      this.$store.commit('events/setIsDisplayDetail', true)
      this.scrollToDate(date)
    },
    toDetailEvent(event) {
      this.$store.commit('events/setIsDisplayDetail', true)
      this.scrollAndOpenEvent(event.id)
    }
  }
}
</script>

<style lang="scss" scoped>
/** goal listと同じようなLayoutをLayout層を作らずに実装する必要があったのでこちらを記載 */

.events {
  position: relative;
  padding-bottom: 56px;
}
/** ここまで */

.calendar{
  position: sticky;
  background-color: $grey02;
  top: 0;
  padding-top: constant(60px + env(safe-area-inset-top));
  padding-top: calc(60px + env(safe-area-inset-top));
  padding-bottom: 8px;
  z-index: 1;

  &__btn{
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background-color: $white;
    vertical-align: middle;
    display: inline-block;
    color: $black;
    border: none;
    padding-top: 0px;
    padding-bottom: 0px;
    &--previous{
      padding-left: 6px;
    }
    &--next{
      padding-left: 6px;
    }
  }
  &__icon{
    line-height: 36px;
  }
  &__date{
    font-size: $fs-nm;
    font-weight: bold;
    line-height: 36px;
  }
}

.datepicker_now ::v-deep  > button{
  position: absolute;
}
.datepicker_now ::v-deep >button > svg{
  display: none;
}

.datepicker_now ::v-deep label{
  background-color :$grey02 !important;
  font-size: 16px;
  font-weight: 700;
}

.event-list {
  display: flex;
  flex-direction: column;
}
</style>